import React, {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {useNavigate, useParams} from "react-router-dom";

import {Checkbox} from "primereact/checkbox";
import {Tag} from "primereact/tag";

import clubStore from "../../../storage/ClubStore";
import appStore from "../../../storage/AppStore";
import systemStore from "../../../storage/SystemStore";

import Btn from "../../../ui/Btn";
import PageLayout from "../../PageLayout";

import "../../../common/billing.css";


const BILLING_TRAINING = 1;


const BillingGroupDetail = () => {
    const {id} = useParams();
    const navigate = useNavigate();

    const group = clubStore.billing.group;
    const bills = clubStore.billing.group_billings || [];
    const members = clubStore.billing.members || [];
    const currency = clubStore.billing.currency || {};

    const [selectedIds, setSelectedIds] = useState([]);
    const [saving, setSaving] = useState(false);


    const loadMembers = async (billingGroup = group) => {
        if (!billingGroup?.id) {
            return;
        }

        await clubStore.billing.getMembers({
            team_id:
                billingGroup.billing_type_id === BILLING_TRAINING
                    ? billingGroup.object_id
                    : null,

            billing_group_id: billingGroup.id
        });
    };


    const load = async () => {
        setSelectedIds([]);

        const billingGroup = await clubStore.billing.getGroup(
            Number(id)
        );

        if (!billingGroup) {
            return;
        }

        await loadMembers(billingGroup);
    };


    useEffect(() => {
        load();

        return () => {
            clubStore.billing.resetGroup();
        };
    }, [id]);


    const toggleMember = (companyUserId) => {
        setSelectedIds((current) => {
            if (current.includes(companyUserId)) {
                return current.filter(
                    (id) => id !== companyUserId
                );
            }

            return [
                ...current,
                companyUserId
            ];
        });
    };


    const selectAll = () => {
        setSelectedIds(
            members.map(
                (member) => member.company_user_id
            )
        );
    };


    const clearSelection = () => {
        setSelectedIds([]);
    };


    const addMembers = async () => {
        if (!group.id || selectedIds.length === 0 || saving) return;
        setSaving(true);
        try {
            const res = await clubStore.billing.addMembers(selectedIds, group.id);
            if (!res || res.result !== 0) throw new Error(res?.message || "billing_add_failed");

            if (Number(res.created_count || 0) === 0) {
                console.warn("No bills created:", res, {group_id: group.id, company_user_ids: selectedIds, amount: group.amount});
                systemStore.setError(appStore.tr.billing_add_no_changes || appStore.tr.please_check_data || "No bills were created");
                return;
            }

            await load();
            systemStore.setSuccess(appStore.tr.successfully_saved);
        } catch (error) {
            console.error("--billing group add members error", error);
            systemStore.setError(appStore.tr[error?.message] || appStore.tr.save_error || error?.message || "Error");
        } finally {
            setSaving(false);
        }
    };


    const deleteBilling = async (billingId) => {
        if (!confirm(appStore.tr.are_you_sure_delete)) return;
        try {
            await clubStore.billing.deleteBilling(billingId);
            await load();
        } catch (error) {
            console.error("--billing group delete error", error);
            systemStore.setError(appStore.tr[error?.message] || error?.message || "Error");
        }
    };


    const getPaymentSeverity = (billing) => {
        if (!billing.is_paid) {
            return "danger";
        }

        if (!billing.is_approved) {
            return "warning";
        }

        return "success";
    };


    const getPaymentLabel = (billing) => {
        if (!billing.is_paid) {
            return appStore.tr.unpaid;
        }

        if (!billing.is_approved) {
            return appStore.tr.not_approved;
        }

        return appStore.tr.paid;
    };


    return (
        <PageLayout
            header={
                <div className="flex justify-content-between align-items-center w-full">

                    <div>
                        <h3 className="m-0">
                            {group.name || appStore.tr.billing}
                        </h3>

                        {!!group.billing_type?.name && (
                            <div className="text-600 mt-1">
                                {group.billing_type.name}
                            </div>
                        )}
                    </div>

                    <Btn
                        icon="pi pi-arrow-left"
                        severity="secondary"
                        outlined
                        onClick={() =>
                            navigate(
                                "/manager/billing/info"
                            )
                        }
                    />

                </div>
            }
        >

            {/* GROUP INFO */}

            <div className="col-12">

                <div className="game-card">

                    <div className="flex flex-column md:flex-row justify-content-between gap-3">

                        <div>

                            {group.object?.name && (
                                <div className="mb-2">

                                    <span className="text-600">
                                        {appStore.tr.payment_purpose || "Объект"}:
                                    </span>

                                    {" "}

                                    <strong>
                                        {group.object.name}
                                    </strong>

                                </div>
                            )}


                            <div className="mb-2">

                                <span className="text-600">
                                    {appStore.tr.billing_period}:
                                </span>

                                {" "}

                                <strong>
                                    {group.period_from}

                                    {group.period_to &&
                                    group.period_to !== group.period_from
                                        ? ` — ${group.period_to}`
                                        : ""
                                    }
                                </strong>

                            </div>


                            {!!group.amount && (
                                <div>

                                    <span className="text-600">
                                        {appStore.tr.amount}:
                                    </span>

                                    {" "}

                                    <strong>
                                        {group.amount}
                                        {currency.sign || ""}
                                    </strong>

                                </div>
                            )}

                        </div>


                        <div className="flex flex-wrap gap-2 align-content-start">

                            <Tag severity="info">
                                {(appStore.tr.members || "Участники")}
                                : {group.billing_count}
                            </Tag>

                            <Tag severity="success">
                                {appStore.tr.paid}
                                : {group.paid_count}
                            </Tag>

                            {group.unpaid_count > 0 && (
                                <Tag severity="danger">
                                    {appStore.tr.unpaid}
                                    : {group.unpaid_count}
                                </Tag>
                            )}

                        </div>

                    </div>


                    <div className="grid mt-3">

                        <div className="col-12 md:col-4">
                            <div className="text-600">
                                {appStore.tr.total || "Всего"}
                            </div>

                            <strong className="text-xl">
                                {group.total_amount}
                                {currency.sign || ""}
                            </strong>
                        </div>


                        <div className="col-12 md:col-4">
                            <div className="text-600">
                                {appStore.tr.paid}
                            </div>

                            <strong className="text-xl">
                                {group.paid_amount}
                                {currency.sign || ""}
                            </strong>
                        </div>


                        <div className="col-12 md:col-4">
                            <div className="text-600">
                                {appStore.tr.debt || "Долг"}
                            </div>

                            <strong className="text-xl">
                                {group.debt_amount}
                                {currency.sign || ""}
                            </strong>
                        </div>

                    </div>

                </div>

            </div>


            {/* BILLINGS */}

            <div className="col-12 mt-3">

                <h4>
                    {appStore.tr.members || "Участники"}
                    {" "}
                    ({bills.length})
                </h4>

            </div>


            {bills.length === 0 && (
                <div className="col-12 text-center text-600">
                    {appStore.tr.not_found}
                </div>
            )}


            {bills.map((billing) => (

                <div
                    className="col-12"
                    key={billing.id}
                >

                    <div className="billing-card">

                        <div className="billing-top">

                            <div className="billing-main">

                                <div className="billing-title">
                                    {billing.company_user?.title}
                                </div>


                                {billing.notes && (
                                    <div className="billing-notes">
                                        {billing.notes}
                                    </div>
                                )}

                            </div>


                            <div className="billing-side">

                                <div className="billing-amount">

                                    {billing.amount}

                                    {billing.currency?.sign ||
                                        currency.sign ||
                                        ""
                                    }

                                </div>


                                <div className="billing-status">

                                    <Tag
                                        severity={
                                            getPaymentSeverity(
                                                billing
                                            )
                                        }
                                    >
                                        {
                                            getPaymentLabel(
                                                billing
                                            )
                                        }
                                    </Tag>


                                    {billing.is_paid &&
                                        billing.payment_method && (
                                            <Tag
                                                severity="info"
                                                className="ml-1"
                                            >
                                                {
                                                    billing.payment_method === "cash"
                                                        ? appStore.tr.cash
                                                        : appStore.tr.transfer
                                                }
                                            </Tag>
                                        )}

                                </div>

                            </div>

                        </div>


                        <div className="billing-actions">

                            <Btn
                                icon="pi pi-eye"
                                severity="success"
                                outlined
                                onClick={() =>
                                    navigate(
                                        `/manager/billing/detail/${billing.id}`
                                    )
                                }
                            />


                            {!billing.is_paid && (
                                <Btn
                                    icon="pi pi-trash"
                                    severity="danger"
                                    outlined
                                    onClick={() =>
                                        deleteBilling(
                                            billing.id
                                        )
                                    }
                                />
                            )}

                        </div>

                    </div>

                </div>

            ))}


            {/* ADD MEMBERS */}

            <div className="col-12 mt-4">

                <div className="game-card">

                    <div className="flex justify-content-between align-items-center mb-3">

                        <div>

                            <h4 className="m-0">
                                {appStore.tr.add_member || "Добавить участников"}
                            </h4>

                            <div className="text-600 mt-1">
                                {members.length}
                                {" "}
                                {appStore.tr.available || "доступно"}
                            </div>

                        </div>


                        {members.length > 0 && (
                            <div className="flex gap-2">

                                <Btn
                                    label={
                                        appStore.tr.select_all ||
                                        "Выбрать всех"
                                    }
                                    severity="secondary"
                                    outlined
                                    onClick={selectAll}
                                />

                                {selectedIds.length > 0 && (
                                    <Btn
                                        icon="pi pi-times"
                                        severity="secondary"
                                        outlined
                                        onClick={clearSelection}
                                    />
                                )}

                            </div>
                        )}

                    </div>


                    {members.length === 0 && (
                        <div className="text-center text-600 py-3">
                            {appStore.tr.not_found}
                        </div>
                    )}


                    {members.map((member) => {

                        const selected =
                            selectedIds.includes(
                                member.company_user_id
                            );

                        return (

                            <div
                                key={member.company_user_id}
                                className="flex align-items-center justify-content-between py-2 border-bottom-1 surface-border"
                                role="button"
                                onClick={() =>
                                    toggleMember(
                                        member.company_user_id
                                    )
                                }
                            >

                                <div className="flex align-items-center gap-3">

                                    <Checkbox
                                        checked={selected}
                                        onChange={() =>
                                            toggleMember(
                                                member.company_user_id
                                            )
                                        }
                                        onClick={(e) =>
                                            e.stopPropagation()
                                        }
                                    />

                                    <span>
                                        {member.title}
                                    </span>

                                </div>


                                {member.amount && (
                                    <span className="text-600">
                                        {member.amount}
                                        {currency.sign || ""}
                                    </span>
                                )}

                            </div>

                        );

                    })}


                    {selectedIds.length > 0 && (

                        <div className="mt-3">

                            <Btn
                                className="w-full justify-content-center"
                                icon="pi pi-plus"
                                severity="success"
                                label={
                                    `${appStore.tr.add || "Добавить"} (${selectedIds.length})`
                                }
                                onClick={addMembers}
                                disabled={saving}
                            />

                        </div>

                    )}

                </div>

            </div>

        </PageLayout>
    );
};


export default observer(BillingGroupDetail);